import React, { useRef } from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  Form, Input, Select, Tag,
} from "antd";
import useFormInstance from "antd/es/form/hooks/useFormInstance";

import {
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../../Common/UIComponents/SelectPopupScrollbar";

import AddressSection from "../CompanyContactForm/Components/AddressSection";

import { validateEmail } from "../../../../Common/utils/validationUtils";

import { ReactComponent as CloseIcon } from "../../../../../assets/images/onboarding/clearfield_2.svg";

import "../CompanyInfoForm.css";
import "../CompanyContactForm/Components/AddressSection.css";

const LOCATION_ADDRESS_TITLES = {
  existing: "Location Address (Manage under existing account)",
  independent: "Location Address",
};

export default function AddLocationDetails({
  companyInfo = {},
  managementType,
}) {
  const form = useFormInstance();
  const emailValidationCache = useRef(new Map());
  const adminEmail = companyInfo.distribution_list?.[0] || companyInfo.customer_email;
  const isIndependent = managementType === "independent";

  const cachedValidateEmail = (string) => {
    if (emailValidationCache.current.has(string)) {
      return emailValidationCache.current.get(string);
    }
    const isValid = validateEmail(string);
    emailValidationCache.current.set(string, isValid);
    return isValid;
  };

  const onDeselect = (value) => {
    if (value === adminEmail) {
      form.setFieldValue("distribution_list", [value]);
      form.validateFields(["distribution_list"]);
    }
  };

  const tagRender = ({ label, onClose }) => {
    const onPreventMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const isAdminEmail = label === adminEmail;
    const validEmail = cachedValidateEmail(label);
    return (
      <Tag
        color={validEmail ? "default" : "error"}
        icon={validEmail ? undefined : <ExclamationCircleOutlined />}
        onMouseDown={onPreventMouseDown}
        onClose={onClose}
        closable={!isAdminEmail}
        className={isAdminEmail ? "CompanyInfoForm__Tag_Admin" : "CompanyInfoForm__Tag"}
        closeIcon={<CloseIcon />}
      >
        {label}
      </Tag>
    );
  };

  const distributionListValidator = async (_, emails) => {
    if (!emails?.length || emails.every((email) => cachedValidateEmail(email))) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Please remove invalid email(s)."));
  };

  const adminNameField = (
    <Form.Item
      name="customer_name"
      label={<span className="CompanyContactForm__ContactInfoSection_FieldsText">Admin Name</span>}
      rules={[{ required: true, message: "" }]}
      className={isIndependent ? "AddressSection__ShortInput" : undefined}
    >
      <Input
        placeholder={isIndependent ? "Enter Text" : "Enter Admin Name"}
        allowClear={{ clearIcon: <CloseIcon className="Clear__Button" /> }}
      />
    </Form.Item>
  );

  return (
    <div className="CompanyContactForm__ContactInfoSection">
      <div className="OnboardingFormSubTitle OnboardingFormSubTitle--first">
        <span />
        <p className="OnboardingFormSubTitleText">Customer Info</p>
      </div>

      <Form.Item
        name="company_name"
        label={<span className="CompanyContactForm__ContactInfoSection_FieldsText">Company Name</span>}
        rules={[{ required: true, message: "" }]}
      >
        <Input
          placeholder="Enter Company Name"
          allowClear={{ clearIcon: <CloseIcon className="Clear__Button" /> }}
        />
      </Form.Item>

      {adminNameField}

      <Form.Item
        name="distribution_list"
        label={<span className="CompanyContactForm__ContactInfoSection_FieldsText">Distribution List</span>}
        rules={[
          { required: true, message: "" },
          { validator: distributionListValidator },
        ]}
      >
        <Select
          mode="tags"
          open={false}
          classNames={selectPopupClassNames()}
          virtual={false}
          listHeight={256}
          popupRender={(menu) => renderSelectPopupScrollbar(menu, {
            optionCount: 0,
          })}
          placeholder={isIndependent
            ? "Enter Text"
            : "Enter list of emails separated by commas. Ex: email@domain.com,email2@domain.com"}
          suffixIcon={null}
          tokenSeparators={[",", " ", ";"]}
          tagRender={tagRender}
          onDeselect={onDeselect}
          onChange={(value) => {
            if (form.getFieldValue("distribution_list").length === 0 && value.length === 0) {
              form.setFieldsValue({ distribution_list: [adminEmail].filter(Boolean) });
            }
          }}
          className="CompanyInfoForm__TagSelect"
        />
      </Form.Item>

      <div className="OnboardingFormSubTitle">
        <span />
        <p className="OnboardingFormSubTitleText">
          {LOCATION_ADDRESS_TITLES[managementType] || "Location Address"}
        </p>
      </div>

      <AddressSection
        namePath={["address"]}
        disabled={false}
      />
    </div>
  );
}
